'use client'

import { Animation, Button, EmptyState, Loader } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { Grid as ItemGrid } from '@marketplace-web/common-components/grid-ui'
import { useAsset } from '@marketplace-web/domain/assets'
import {
  getBumpableItemDataFromModel,
  BumpableItemModel,
  clickListAnItemEvent,
} from '@marketplace-web/vas/vas-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { navigateToPage } from '@marketplace-web/shared/browser'

import { ITEM_UPLOAD_URL } from 'constants/routes'
import { Screen } from 'constants/tracking/screens'

import { ErrorBoundary } from '@marketplace-web/shared/error-display'

import useBumpCheckoutContext from '../../../hooks/useBumpCheckoutContext'
import BumpableItemBox from '../BumpableItemBox'

type Props = {
  items: Array<BumpableItemModel>
  isLoading: boolean
  endReached: boolean
  loadItems: () => void
  onItemVisible: ComponentProps<typeof ItemGrid<BumpableItemModel>>['onItemVisible']
  onItemClick: (item: BumpableItemModel, position: number, isSelected: boolean) => void
}

const BumpItemsMultiselect = ({
  items,
  isLoading,
  endReached,
  loadItems,
  onItemVisible,
  onItemClick,
}: Props) => {
  const { track } = useTracking()
  const asset = useAsset('/assets/animations')
  const translateEmptyState = useTranslate('bump_items.empty_state.multi_bump')
  const translateMultiselectActions = useTranslate('items_multiselect.actions')

  const { selectedItems, setSelectedItems } = useBumpCheckoutContext()

  const selectItem = (item: BumpableItemModel, index: number) => () => {
    const isItemSelected = selectedItems.some(selectedItem => selectedItem.id === item.id)

    const updatedItems = isItemSelected
      ? selectedItems.filter(selectedItem => selectedItem.id !== item.id)
      : [...selectedItems, getBumpableItemDataFromModel(item)]

    setSelectedItems(updatedItems)

    onItemClick(item, index, !isItemSelected)
  }

  const handleEmptyStateActionClick = () => {
    track(clickListAnItemEvent({ screen: Screen.MultiplePushUp }))

    navigateToPage(ITEM_UPLOAD_URL)
  }

  const renderItemFooter = (item: BumpableItemModel, index: number) => {
    const isItemSelected = selectedItems.some(selectedItem => selectedItem.id === item.id)
    const buttonTextSuffix = isItemSelected ? 'remove' : 'add'

    return (
      <Button
        text={translateMultiselectActions(`${buttonTextSuffix}`)}
        theme={isItemSelected ? 'warning' : 'primary'}
        size={Button.Size.Small}
        onClick={selectItem(item, index)}
        testId={`${buttonTextSuffix}-button`}
      />
    )
  }

  const renderItem: ComponentProps<typeof ItemGrid<BumpableItemModel>>['renderItem'] = (
    { data },
    index,
  ) => {
    return (
      <ErrorBoundary FallbackComponent={ErrorBoundary.ComponentError}>
        <BumpableItemBox
          item={data}
          renderFooter={renderItemFooter(data, index)}
          onClick={selectItem(data, index)}
        />
      </ErrorBoundary>
    )
  }

  const renderItemsContainer = () => {
    const gridItems = items.map(item => ({ id: item.id, data: item }))

    return (
      <ItemGrid
        items={gridItems}
        renderItem={renderItem}
        widthResolver={() => 'one-fifth'}
        onItemVisible={onItemVisible}
      />
    )
  }

  return (
    <>
      <InfiniteScroll
        renderItems={renderItemsContainer}
        onPageEnd={loadItems}
        cursor={String(items.length)}
      />
      {isLoading && (
        <div className="u-flexbox u-justify-content-center u-padding-vertical-large">
          <Loader size={Loader.Size.Large} testId="items-multiselect--loader" />
        </div>
      )}
      {!isLoading && endReached && !items.length && (
        <div className="u-flexbox u-justify-content-center u-flex-grow">
          <EmptyState
            title={translateEmptyState('heading')}
            body={translateEmptyState('body')}
            animation={<Animation animationUrl={asset('closet-empty-state.json')} />}
            testId="items-multiselect-empty-state"
            action={
              <Button
                text={translateEmptyState('action')}
                onClick={handleEmptyStateActionClick}
                styling={Button.Styling.Filled}
                testId="items-multiselect-empty-state--action-button"
              />
            }
          />
        </div>
      )}
    </>
  )
}

export default BumpItemsMultiselect
